import { BACKEND_URL, PUSHER_KEY, PUSHER_CLUSTER, PUSHER_ENCRYPTED } from '@/config/app';
import { useAuthStore } from '@/stores';
import useAlert from '@/composables/useAlert';

class RealtimeMessagingService {
  constructor() {
    this.activeChannels = new Map();
    this.messageHandlers = new Map();
    this.callHandlers = new Map();
    this.authStore = null;
    this.alert = null;
    this.echo = null;
    this.initialized = false;
  }

  /**
   * Get the auth store (lazy initialization)
   */
  getAuthStore() {
    if (!this.authStore) {
      this.authStore = useAuthStore();
    }
    return this.authStore;
  }

  /**
   * Get the alert function (lazy initialization)
   */
  getAlert() {
    if (!this.alert) {
      this.alert = useAlert();
    }
    return this.alert;
  }

  /**
   * Initialize Echo for real-time messaging
   */
  async initializeEcho() {
    if (this.initialized) return;
    
    try {
      // Check if Echo is already initialized (e.g., from DashboardLayout)
      if (window.Echo) {
        console.log('Using existing Echo instance from DashboardLayout');
        this.echo = window.Echo;
        this.initialized = true;
        return;
      }
      
      // Dynamic import to avoid SSR issues
      const { default: Echo } = await import('laravel-echo');
      const { default: Pusher } = await import('pusher-js');
      
      // Following the pattern in DashboardLayout.vue
      Pusher.logToConsole = false;
      
      Pusher.Runtime.createXHR = function () {
        var xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        return xhr;
      };
      
      // Initialize Pusher and Echo - use same config as DashboardLayout
      window.Pusher = Pusher;
      window.Echo = new Echo({
        broadcaster: 'pusher',
        key: PUSHER_KEY,
        cluster: PUSHER_CLUSTER,
        encrypted: PUSHER_ENCRYPTED,
        channelAuthorization: {
          endpoint: BACKEND_URL + "api/pusher",
          withCredentials: true,
        },
      });
      
      this.echo = window.Echo;
      this.initialized = true;
      console.log('Initialized new Echo instance for real-time messaging');
    } catch (error) {
      console.error('Error initializing Echo for messaging:', error);
      throw error;
    }
  }

  /**
   * Join a conversation channel
   * @param {string} conversationId - The conversation ID
   * @param {Object} options - Configuration options
   */
  async joinConversation(conversationId, options = {}) {
    if (this.activeChannels.has(conversationId)) {
      console.warn(`Already listening to conversation: ${conversationId}`);
      return;
    }

    // Initialize Echo if not already done
    if (!this.initialized) {
      await this.initializeEcho();
    }

    try {
      const channel = this.echo.private(`conversation.${conversationId}`);
      
      // Listen for new messages
      channel.listen('.message.sent', (e) => {
        console.log('New message received:', e);
        this.handleNewMessage(e, options);
      });

      // Listen for typing indicators
      channel.listen('.typing.started', (e) => {
        this.handleTypingStarted(e, options);
      });

      channel.listen('.typing.stopped', (e) => {
        this.handleTypingStopped(e, options);
      });

      // Listen for call events
      channel.listen('.call.requested', (e) => {
        this.handleCallRequested(e, options);
      });

      channel.listen('.call.ended', (e) => {
        this.handleCallEnded(e, options);
      });

      channel.listen('.call.rejected', (e) => {
        this.handleCallRejected(e, options);
      });

      channel.listen('.call.accepted', (e) => {
        this.handleCallAccepted(e, options);
      });

      // Store the channel reference
      this.activeChannels.set(conversationId, channel);
      
      console.log(`Joined conversation channel: ${conversationId}`);
      
         } catch (error) {
       console.error('Error joining conversation channel:', error);
       this.getAlert()('Failed to connect to real-time messaging', 'danger');
     }
  }

  /**
   * Join global messages channel for current user
   * @param {Object} options - Configuration options
   */
  async joinGlobalMessages(options = {}) {
    const currentUser = this.getAuthStore().user;
    if (!currentUser?.id) {
      console.error('Cannot join global messages: No authenticated user');
      return;
    }

    if (this.activeChannels.has('global-messages')) {
      console.warn('Already listening to global messages channel');
      return;
    }

    // Initialize Echo if not already done
    if (!this.initialized) {
      await this.initializeEcho();
    }

    try {
      const channel = this.echo.private(`user.${currentUser.id}.messages`);
      
      // Listen for global message updates
      channel.listen('.conversation.updated', (e) => {
        console.log('Global conversation update received:', e);
        this.handleGlobalConversationUpdate(e, options);
      });

      // Listen for new conversation events
      channel.listen('.conversation.created', (e) => {
        console.log('New conversation created:', e);
        this.handleNewConversation(e, options);
      });

      // Listen for unread count updates
      channel.listen('.unread.updated', (e) => {
        console.log('Unread count updated:', e);
        this.handleUnreadCountUpdate(e, options);
      });

      // Store the channel reference
      this.activeChannels.set('global-messages', channel);
      
      console.log(`Joined global messages channel for user: ${currentUser.id}`);
      
    } catch (error) {
      console.error('Error joining global messages channel:', error);
      this.getAlert()('Failed to connect to global messages', 'danger');
    }
  }

  /**
   * Leave a conversation channel
   * @param {string} conversationId - The conversation ID
   */
  leaveConversation(conversationId) {
    const channel = this.activeChannels.get(conversationId);
    if (channel) {
      channel.stopListening('.message.sent');
      channel.stopListening('.typing.started');
      channel.stopListening('.typing.stopped');
      channel.stopListening('.call.requested');
      channel.stopListening('.call.ended');
      channel.stopListening('.call.rejected');
      channel.stopListening('.call.accepted');
      
      this.activeChannels.delete(conversationId);
      console.log(`Left conversation channel: ${conversationId}`);
    }
  }

  /**
   * Leave global messages channel
   */
  leaveGlobalMessages() {
    const channel = this.activeChannels.get('global-messages');
    if (channel) {
      channel.stopListening('.conversation.updated');
      channel.stopListening('.conversation.created');
      channel.stopListening('.unread.updated');
      
      this.activeChannels.delete('global-messages');
      console.log('Left global messages channel');
    }
  }

  /**
   * Handle new message events
   * @param {Object} event - The message event data
   * @param {Object} options - Configuration options
   */
  handleNewMessage(event, options = {}) {
    const { message_type, sender_id } = event;
    
    // Don't show notifications for own messages
    const currentUser = this.getAuthStore().user;
    if (sender_id === currentUser?.id) {
      return;
    }

    // Handle different message types
    switch (message_type) {
      case 'text':
        this.handleTextMessage(event, options);
        break;
      case 'file':
        this.handleFileMessage(event, options);
        break;
      case 'missed_call':
        this.showMissedCallNotification(event, options);
        break;
      case 'video_call_request':
        this.showVideoCallRequest(event, options);
        break;
      case 'voice_call_request':
        this.showVoiceCallRequest(event, options);
        break;
      case 'call_ended':
        this.showCallEndedNotification(event, options);
        break;
      case 'call_rejected':
        this.showCallRejectedNotification(event, options);
        break;
      case 'call_accepted':
        this.showCallAcceptedNotification(event, options);
        break;
      case 'system':
        this.handleSystemMessage(event, options);
        break;
      default:
        console.log('Unknown message type:', message_type);
    }

    // Emit to registered handlers
    this.emitToHandlers('message', event, options);
  }

  /**
   * Handle text messages
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  handleTextMessage(event, options) {
    const { sender, content } = event;
    
    // Show notification if enabled
    if (options.showNotifications !== false) {
      this.getAlert()(`${sender.name}: ${content}`, 'info');
    }

    // Emit to text message handlers
    this.emitToHandlers('text-message', event, options);
  }

  /**
   * Handle file messages
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  handleFileMessage(event, options) {
    const { sender, file_name } = event;
    
    // Show notification if enabled
    if (options.showNotifications !== false) {
      this.getAlert()(`${sender.name} sent a file: ${file_name}`, 'info');
    }

    // Emit to file message handlers
    this.emitToHandlers('file-message', event, options);
  }

  /**
   * Show missed call notification
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  showMissedCallNotification(event, options) {
    const { sender, metadata } = event;
    const callType = metadata?.call_type || 'call';
    
    this.getAlert()(`Missed ${callType} from ${sender.name}`, 'warning');
    this.emitToHandlers('missed-call', event, options);
  }

  /**
   * Show video call request
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  showVideoCallRequest(event, options) {
    const { sender } = event;
    
    // You can implement a custom modal here
    this.getAlert()(`Video call request from ${sender.name}`, 'info');
    this.emitToHandlers('video-call-request', event, options);
  }

  /**
   * Show voice call request
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  showVoiceCallRequest(event, options) {
    const { sender } = event;
    
    // You can implement a custom modal here
    this.getAlert()(`Voice call request from ${sender.name}`, 'info');
    this.emitToHandlers('voice-call-request', event, options);
  }

  /**
   * Show call ended notification
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  showCallEndedNotification(event, options) {
    const { sender, metadata } = event;
    const duration = metadata?.duration || 0;
    
    this.getAlert()(`Call ended with ${sender.name} (${duration}s)`, 'info');
    this.emitToHandlers('call-ended', event, options);
  }

  /**
   * Show call rejected notification
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  showCallRejectedNotification(event, options) {
    const { sender, metadata } = event;
    const reason = metadata?.reason || 'No reason provided';
    
    this.getAlert()(`Call rejected by ${sender.name}: ${reason}`, 'warning');
    this.emitToHandlers('call-rejected', event, options);
  }

  /**
   * Show call accepted notification
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  showCallAcceptedNotification(event, options) {
    const { sender } = event;
    
    this.getAlert()(`Call accepted by ${sender.name}`, 'success');
    this.emitToHandlers('call-accepted', event, options);
  }

  /**
   * Handle system messages
   * @param {Object} event - The message event
   * @param {Object} options - Configuration options
   */
  handleSystemMessage(event, options) {
    const { content } = event;
    
    this.getAlert()(content, 'info');
    this.emitToHandlers('system-message', event, options);
  }

  /**
   * Handle typing started
   * @param {Object} event - The typing event
   * @param {Object} options - Configuration options
   */
  handleTypingStarted(event, options) {
    const { sender_id } = event;
    
    // Don't show typing for own messages
    const currentUser = this.getAuthStore().user;
    if (sender_id === currentUser?.id) {
      return;
    }

    this.emitToHandlers('typing-started', event, options);
  }

  /**
   * Handle typing stopped
   * @param {Object} event - The typing event
   * @param {Object} options - Configuration options
   */
  handleTypingStopped(event, options) {
    const { sender_id } = event;
    
    // Don't show typing for own messages
    const currentUser = this.getAuthStore().user;
    if (sender_id === currentUser?.id) {
      return;
    }

    this.emitToHandlers('typing-stopped', event, options);
  }

  /**
   * Handle call requested
   * @param {Object} event - The call event
   * @param {Object} options - Configuration options
   */
  handleCallRequested(event, options) {
    this.emitToHandlers('call-requested', event, options);
  }

  /**
   * Handle call ended
   * @param {Object} event - The call event
   * @param {Object} options - Configuration options
   */
  handleCallEnded(event, options) {
    this.emitToHandlers('call-ended', event, options);
  }

  /**
   * Handle call rejected
   * @param {Object} event - The call event
   * @param {Object} options - Configuration options
   */
  handleCallRejected(event, options) {
    this.emitToHandlers('call-rejected', event, options);
  }

  /**
   * Handle call accepted
   * @param {Object} event - The call event
   * @param {Object} options - Configuration options
   */
  handleCallAccepted(event, options) {
    this.emitToHandlers('call-accepted', event, options);
  }

  /**
   * Handle global conversation update
   * @param {Object} event - The global conversation update event
   * @param {Object} options - Configuration options
   */
  handleGlobalConversationUpdate(event, options) {
    const conversation = event;
    const currentUser = this.getAuthStore().user;
    conversation.unread_count = event.unread_counts[currentUser.id];
    
    // Emit to global conversation update handlers
    this.emitToHandlers('global-conversation-updated', conversation, options);
  }

  /**
   * Handle new conversation
   * @param {Object} event - The new conversation event
   * @param {Object} options - Configuration options
   */
  handleNewConversation(event, options) {
    const conversation = event;
    
    // Emit to new conversation handlers
    // this.emitToHandlers
    ('new-conversation', conversation, options);
  }

  /**
   * Handle unread count update
   * @param {Object} event - The unread count update event
   * @param {Object} options - Configuration options
   */
  handleUnreadCountUpdate(event, options) {
    const { conversation_id, unread_count } = event;
    
    // Emit to unread count update handlers
    this.emitToHandlers('unread-count-updated', { conversation_id, unread_count }, options);
  }

  /**
   * Register a message handler
   * @param {string} eventType - The event type to listen for
   * @param {Function} handler - The handler function
   */
  on(eventType, handler) {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, []);
    }
    this.messageHandlers.get(eventType).push(handler);
  }

  /**
   * Register a call handler
   * @param {string} eventType - The event type to listen for
   * @param {Function} handler - The handler function
   */
  onCall(eventType, handler) {
    if (!this.callHandlers.has(eventType)) {
      this.callHandlers.set(eventType, []);
    }
    this.callHandlers.get(eventType).push(handler);
  }

  /**
   * Remove a message handler
   * @param {string} eventType - The event type
   * @param {Function} handler - The handler function to remove
   */
  off(eventType, handler) {
    const handlers = this.messageHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Remove a call handler
   * @param {string} eventType - The event type
   * @param {Function} handler - The handler function to remove
   */
  offCall(eventType, handler) {
    const handlers = this.callHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to registered handlers
   * @param {string} eventType - The event type
   * @param {Object} data - The event data
   * @param {Object} options - Configuration options
   */
  emitToHandlers(eventType, data, options) {
    const handlers = this.messageHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data, options);
        } catch (error) {
          console.error(`Error in ${eventType} handler:`, error);
        }
      });
    }
  }

  /**
   * Send typing indicator
   * @param {string} conversationId - The conversation ID
   * @param {boolean} isTyping - Whether the user is typing
   */
  sendTypingIndicator(conversationId, isTyping) {
    try {
      const channel = this.activeChannels.get(conversationId);
      if (channel) {
        if (isTyping) {
          channel.whisper('typing-started', {});
        } else {
          channel.whisper('typing-stopped', {});
        }
      }
    } catch (error) {
      console.error('Error sending typing indicator:', error);
    }
  }

  /**
   * Clean up all channels
   */
  cleanup() {
    this.activeChannels.forEach((channel, conversationId) => {
      if (conversationId === 'global-messages') {
        this.leaveGlobalMessages();
      } else {
        this.leaveConversation(conversationId);
      }
    });
    
    this.messageHandlers.clear();
    this.callHandlers.clear();
  }
}

// Create and export a singleton instance
const realtimeMessagingService = new RealtimeMessagingService();

export default realtimeMessagingService; 